<template>
    <v-app>
        <header-card-public current_page="login"></header-card-public>
        
       <v-content class="bg-container">
            <v-layout fill-height row wrap>

                <!-- Left Side: Notifications (Desktop Only) -->
                <v-flex v-if="!isMobile" xs12 sm6 class="notifications-section">
                    <div class="notifications-bg">
                        <!-- <public-notifications></public-notifications> -->
                        <platform-showcase></platform-showcase>
                    </div>
                </v-flex>

                <!-- Right/bottom Side: Welcome Card -->
                <v-flex xs12 sm6 :class="welcomeSectionClass">

                    <!-- Main container centering -->
                    <v-layout fill-height align-center justify-center>

                        <v-layout column align-center justify-center class="right-panel-content">

                            <v-card :class="isMobile ? 'welcome-card-mobile' : 'welcome-card'" flat>

                                <v-form @submit.prevent="executeRecaptcha" ref="form">
                                    <v-card-text>
                                        <!-- 📝 Updated: Switched to crisp slate blue typography header -->
                                        <h3 v-if="isRecaptchaLoaded" class="text-xs-center welcome-message card-dark-text">Sign in with password</h3>
                                        <h3 v-if="!isRecaptchaLoaded" class="text-xs-center welcome-message card-dark-text">Loading components...</h3>

                                        <v-alert v-if="error" :value="true" color="warning" icon="warning" class="rounded-4 mt-10" outline>
                                            {{errorMessage}}
                                        </v-alert>
                                        
                                        <v-alert v-if="slowInternet" :value="true" color="warning" icon="warning" class="rounded-4 mt-10" outline>
                                            Internet connection is slow. Some features may not work properly. 
                                        </v-alert>

                                        <!-- 🎨 Input Overrides: Switched accent colors to our primary brand blue -->
                                        <v-text-field
                                            class="body-1 mt-20 brand-input-field"
                                            prepend-inner-icon="email"
                                            color="#1976D2"
                                            v-model="email"
                                            label="Email"
                                            :rules="[rules.required,rules.email,rules.min,rules.max]"
                                            type="text"
                                            :disabled="disabled||isNoInternet"
                                        ></v-text-field>

                                        <v-text-field
                                            class="body-1 brand-input-field"
                                            prepend-inner-icon="lock"
                                            color="#1976D2"
                                            v-model="password"
                                            :append-icon="show1 ? 'visibility_off' : 'visibility'"
                                            :rules="[rules.required,rules.counter]"
                                            :type="show1 ? 'text' : 'password'"
                                            label="Password"
                                            @click:append="show1 = !show1"
                                            :disabled="disabled||isNoInternet"
                                        ></v-text-field>

                                        <!-- 🔘 Buttons: Hardcoded to #1976D2 primary state -->
                                        <v-btn v-if="!slowInternet" class="no-uppercase rounded-4 primary-device-btn mt-4" color="#1976D2" type="submit" :loading="loading" block dark :disabled="isNoInternet">
                                            Sign In
                                        </v-btn>
                                        <v-btn v-if="slowInternet" class="no-uppercase rounded-4 primary-device-btn mt-4" color="#1976D2" block dark @click="reloadPage">Refresh Page</v-btn>
                                    </v-card-text>
                                </v-form>
                                
                                <!-- Invisible separation line to preserve glass look -->
                                <div class="glass-divider"></div>
                                
                                <v-card-actions class="h40 px-3">
                                    <!-- Back button inheriting brand colors -->
                                    <v-btn icon title="Back to sign-in options" class="card-action-back-btn" @click="$router.push('/signin-options')" :disabled="disabled||isNoInternet">
                                        <v-icon color="#1565C0">arrow_back</v-icon>
                                    </v-btn>
                                    <v-spacer></v-spacer>
                                    <!-- Forgot password link utilizing brand text styling safely -->
                                    <v-btn small flat to="/PasswordRecovery" :disabled="disabled||isNoInternet||!isRecaptchaLoaded" class="caption no-uppercase mr-5 recovery-link-btn">
                                        I forgot my password
                                    </v-btn>
                                </v-card-actions>
                            </v-card>

                            <!-- Responsive Footer Area -->
                            <div class="global-desktop-footer caption">
                                <terms-privacy></terms-privacy>
                                <other-legal-docs></other-legal-docs>
                                <copyright></copyright>
                            </div>

                        </v-layout>

                    </v-layout>
                </v-flex>
            </v-layout>
        </v-content>

        <recaptcha ref="recaptcha" @verify="submitReCaptcha" @recaptcha-loaded="recaptchaLoaded"></recaptcha>
        
        <v-snackbar v-model="isNoInternet" color="warning" :bottom="y === 'top'" :left="x === 'left'" :multi-line="mode === 'multi-line'"
            :right="x === 'right'" :timeout="0" :top="y === 'top'" :vertical="mode === 'vertical'"
            >You have no internet connection.
            <v-btn color="primary" flat @click="isNoInternet=false">Close</v-btn>
        </v-snackbar>
    </v-app>
</template>

<script>

import '@/assets/css/public-pages.css'
import auth from "@/auth/auth.js"
import publicPages from '@/helpers/public-pages.js'
import { getUAData } from '@/helpers/user-agent.js'
import { parseApiError } from '@/helpers/login-error-handler'

export default {
    name: 'Login',
    metaInfo: {
        title: 'Login',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: 'Login' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
        ]
    },
    components: {
        'recaptcha': () => import('@/components/ReCaptcha.vue'),
        'platform-showcase': () => import('@/components/PublicNotifications/PlatformShowcase'),
        'terms-privacy': () => import('@/components/Common/TermsAndPrivacy'), 
        'other-legal-docs': () => import('@/components/Common/OtherLegalDocs'),
        'copyright': () => import('@/components/Common/Copyright'),      
        'header-card-public': () => import('@/components/Common/HeaderPublic'),
    },
    data () {
        return {
            loading: true,
            disabled: false,
            error: false,
            errorMessage: null,
            show1: false,
            slowInternet: false,

            //for refactor - create reusable rules-helper.js 
            rules: {
                required: value => !!value || "Required.",
                min: v => v.length >= 12 || "Min 12 characters",
                counter: value => value.length <= 50 || "Max 50 characters",
                max: value => value.length <= 50 || "Max 50 characters",
                email: value => {
                    const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                    return pattern.test(value) || "Invalid e-mail.";
                }
            },
            email: "",
            password: "",
            isNoInternet: false,
            isRecaptchaLoaded: false,
            mode: null,
            x: null,
            y: null,
            recaptchaLoadingTimer: null,
            userAgent: null,
        }
    },
    methods: {
        // =========================
        // UI HELPERS
        // =========================
        setLoadingState(isLoading) {
            this.loading = isLoading
            this.disabled = isLoading
        },
        showError(message) {
            this.error = true
            this.errorMessage = message
        },
        clearError() {
            this.error = false
            this.errorMessage = null
        },
        
        // =========================
        // RECAPTCHA
        // =========================        
        submitReCaptcha(response) {
            this.recaptchResponse = response;   
            this.login()
        },
        executeRecaptcha() {
            if (this.$refs.form.validate()) {
                this.loading = true;

                // execute the recaptcha widget
                this.$refs.recaptcha.execute()
                this.loading = false;
            }
        },
        recaptchaLoaded() {
            this.loading = false
            this.isRecaptchaLoaded = true
        },
        checkRecaptcha() {
            if (!this.isRecaptchaLoaded) {
                this.slowInternet = true
            }else{
                this.slowInternet = false
                clearInterval(this.recaptchaLoadingTimer)
            }
        },

        // =========================
        // LOGIN FLOW
        // =========================
        async login() {
            try {
                this.clearError()

                if (!this.$refs.form.validate()) return

                this.setLoadingState(true)

                const payload = this.buildLoginPayload()
                
                await auth.login(payload, false)

                // ===== HANDLE POST LOGIN =====
                if (auth.requiresChangePassword()) {
                    
                    //if change password has been auto triggered after login eg first login
                    //then must restrict skipping change password
                    auth.deleteToken()

                    // return this.$router.push("/changepassword")
                    return this.$router.push({
                        name: 'ChangePassword', //don't pass the path as this is validated in ChangePassword
                        query: { redirect: this.$route.query.redirect }, // Keep passing it
                        params: { fromLoginFlow: true }, 
                    });
                }

                this.handlePostLoginNavigation()

            } catch (error) {
                //this will handle all non 2xx response codes
                this.showError(parseApiError(error))
            } finally {
                this.setLoadingState(false)
            }
        },
        buildLoginPayload() {
            return {
                email: this.email,
                password: this.password,
                recaptchaResponse: this.recaptchResponse,
                browserName: this.userAgent.browser.name,
                deviceModel: this.userAgent.device.model,
                deviceType: this.userAgent.device.type,
                deviceVendor: this.userAgent.device.vendor,
                oSName: this.userAgent.os.name,
                oSVersion: this.userAgent.os.version,
                deviceFingerprint: this.userAgent.fingerprint,
                deviceName: this.userAgent.deviceName,
                sourceType: 'Customer'
            }
        },
        handlePostLoginNavigation() {
            // ===== 2FA FLOW =====
            if (this.$store.state.twoFA) {
                if (!this.$store.state.twoFAVerified) {
                    auth.deleteToken()
                    // return this.$router.push("/otp")
                    return this.$router.push({
                        path: '/otp',
                        query: { redirect: this.$route.query.redirect } // keep passing target url
                    });
                }

                return this.redirectAfterLogin()
            }

            // ===== NEW DEVICE FLOW =====
            if (this.$store.state.newDevice) {
                auth.deleteToken()
                // return this.$router.push("/otp")
                return this.$router.push({
                    path: '/otp',
                    query: { redirect: this.$route.query.redirect } // keep passing target url
                });
            }

            auth.clearLoginDetails()
            this.redirectAfterLogin()
        },
        redirectAfterLogin() {
            if (auth.onBoarded()) {
                const destination = this.$route.query.redirect || '/'
                this.$router.push(destination)
            } else {
                this.$router.push("/features")
            }
        },

        // =========================
        // CONNECTION
        // =========================
        updateConnectionStatus() {
            this.isNoInternet = !navigator.onLine;
        },
        reloadPage() {
            window.location.reload()
        },
    },
    async created() {
        this.userAgent = await getUAData()

        window.addEventListener('online', this.updateConnectionStatus)
        window.addEventListener('offline', this.updateConnectionStatus)

        this.updateConnectionStatus()

        //if recaptcha is not loaded in 5secs, show slow internet
        this.recaptchaLoadingTimer = setInterval(this.checkRecaptcha, parseInt(5000))  
    },
    computed: {
        seasonDetails() {
            return publicPages.getSeasonDetails(this.$route.query.action, this.isMobile);
        },
        welcomeSectionClass() {
            // Keeps hook open for seasonal transitions if needed, but bounded to structural containers
            return this.seasonDetails.className || 'default-right-panel';
        },
    }
}
</script>